/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   workflowApi.ts
 *     Description     :   API client for workflow status endpoints
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-16
 *     Modified        :   2026-02-16 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.3.0 (Enterprise Dependency Architecture)
 *     Feature         :   135-ui-data-alignment
 * =========================================================================================
 */

import { apiClient } from '../../backup/services/api';
import type {
  WorkflowOnboardingResponse,
  WorkflowSummaryResponse,
  CollectionSyncStatus,
  EntityWorkflowStatus,
} from '../../../shared/types/workflow';

// ─── Query Parameters ────────────────────────────────────────────────────────

export interface WorkflowOnboardingFilters {
  filter?: 'all' | 'complete' | 'incomplete' | 'stuck';
  sortBy?: 'name' | 'completion' | 'currentStep';
  sortDir?: 'asc' | 'desc';
  search?: string;
}

// ─── API Functions ───────────────────────────────────────────────────────────

/**
 * Fetch lakehouse onboarding workflow status for all lakehouses.
 */
export async function fetchLakehouseOnboarding(
  filters: WorkflowOnboardingFilters = {}
): Promise<WorkflowOnboardingResponse> {
  const params: Record<string, string | undefined> = {
    filter: filters.filter,
    sortBy: filters.sortBy,
    sortDir: filters.sortDir,
    search: filters.search,
  };

  // Remove undefined params
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined) delete params[key];
  });

  return apiClient.get<WorkflowOnboardingResponse>(
    '/api/workflows/onboarding/lakehouses',
    params
  );
}

/**
 * Fetch warehouse onboarding workflow status for all warehouses.
 */
export async function fetchWarehouseOnboarding(
  filters: WorkflowOnboardingFilters = {}
): Promise<WorkflowOnboardingResponse> {
  const params: Record<string, string | undefined> = {
    filter: filters.filter,
    sortBy: filters.sortBy,
    sortDir: filters.sortDir,
    search: filters.search,
  };

  Object.keys(params).forEach((key) => {
    if (params[key] === undefined) delete params[key];
  });

  return apiClient.get<WorkflowOnboardingResponse>(
    '/api/workflows/onboarding/warehouses',
    params
  );
}

/**
 * Fetch collection job sync status.
 */
export async function fetchCollectionSyncStatus(): Promise<CollectionSyncStatus> {
  return apiClient.get<CollectionSyncStatus>('/api/workflows/collection/status');
}

/**
 * Fetch aggregated workflow summary across all workflow types.
 */
export async function fetchWorkflowSummary(): Promise<WorkflowSummaryResponse> {
  return apiClient.get<WorkflowSummaryResponse>('/api/workflows/summary');
}

/**
 * Fetch single entity workflow status (for detail page banners).
 */
export async function fetchEntityWorkflowStatus(
  entityType: string,
  entityId: string
): Promise<EntityWorkflowStatus | null> {
  try {
    return await apiClient.get<EntityWorkflowStatus>(
      `/api/workflows/entity/${entityType}/${entityId}`
    );
  } catch (error: any) {
    if (error?.status === 404) {
      return null;
    }
    throw error;
  }
}
